import IcoMoon from '@/components/IcoMoon'
import { useAuth } from '@/context/auth'
import { useBookmarkItems } from '@/context/bookmarkedItems'
import { addBookmark, removeBookmark } from '@/lib/bookmark'
import { ItemType } from '@/shared/api-generated-types'
import React, { useState } from 'react'

export interface BookmarkButtonProps {
  bookmarked?: boolean
  onClick?: () => void
  className?: string
  size?: 'sm' | 'md' | 'lg'
  itemId?: string | null
  itemType?: ItemType
}

const BookmarkButton: React.FC<BookmarkButtonProps> = ({
  bookmarked,
  onClick,
  className = '',
  size = 'md',
  itemId,
  itemType = ItemType.Class,
}) => {
  const { state: auth } = useAuth()
  const { dispatch } = useBookmarkItems()
  const [showAnimation, setShowAnimation] = useState(false)
  const [showText, setShowText] = useState(false)
  const [animationText, setAnimationText] = useState('')

  const handleBookmarkClick = async (e: React.MouseEvent): Promise<void> => {
    e.preventDefault()
    e.stopPropagation()
    if (!itemId) {
      console.warn('BookmarkButton: itemId is required')
      if (onClick) onClick()
      return
    }
    try {
      if (bookmarked) {
        await removeBookmark(itemId)
        setAnimationText('Removed')
        setShowAnimation(true)
        setShowText(true)
        switch (itemType) {
          case ItemType.Class:
            dispatch({ type: 'REMOVE_CLASS', payload: { itemId } })
            break
          case ItemType.Article:
            dispatch({ type: 'REMOVE_ARTICLE', payload: { itemId } })
            break
        }
        if (onClick) onClick()
      } else {
        const result = await addBookmark({ itemId, itemType })
        if (result) {
          setAnimationText('Added')
          setShowAnimation(true)
          setShowText(true)
          switch (itemType) {
            case ItemType.Class:
              dispatch({ type: 'ADD_CLASS', payload: { itemId } })
              break
            case ItemType.Article:
              dispatch({ type: 'ADD_ARTICLE', payload: { itemId } })
              break
          }
          if (onClick) onClick()
        }
      }
    } catch (err) {
      console.error('Bookmark error:', err)
    }
  }

  const sizeClasses = {
    sm: 'h-6 text-xs',
    md: 'h-10 text-sm',
    lg: 'h-12 text-base',
  }

  const widthClasses = {
    sm: showAnimation ? 'w-24' : 'w-6',
    md: showAnimation ? 'w-24' : 'w-10',
    lg: showAnimation ? 'w-32' : 'w-12',
  }

  if (!auth.user) {
    return null
  }

  return (
    <button
      onClick={handleBookmarkClick}
      className={`
      ${bookmarked ? 'bg-secondary' : 'bg-gray-100'}
      ${sizeClasses[size]}
      ${widthClasses[size]}
      ${className}
      z-[4] overflow-hidden transition-all duration-300 ease-in-out hover:shadow-lg
    `}
      aria-label={bookmarked ? 'Remove bookmark' : 'Add bookmark'}
    >
      <div className="flex items-center justify-center">
        <IcoMoon icon="heart" size={size === 'sm' ? 17 : 28} color={bookmarked ? '#ffffff' : '#9ca3af'} />
        <span
          className={`
          ${showAnimation ? 'static ml-2' : 'absolute w-0 h-0 overflow-hidden'}
          font-semibold whitespace-nowrap transition-opacity duration-500
          ${showText ? 'opacity-100' : 'opacity-0'}
          text-xs md:text-md ${bookmarked ? 'text-white' : 'text-gray-500'}
        `}
          onTransitionEnd={() => {
            if (showText) {
              setShowAnimation(false)
              setShowText(false)
            }
          }}
        >
          {animationText}
        </span>
      </div>
    </button>
  )
}

export default BookmarkButton
